import { type ReactNode } from "react";
import { Inbox, Loader2, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Card as CardRoot,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

// Barrel that composes the shadcn primitives into the app-specific surface the
// feature pages consume. Re-exports keep the original import sites unchanged
// while the visuals come from the "Transmission" kit.
export { Button } from "@/components/ui/button";
export { Input } from "@/components/ui/input";
export { Label } from "@/components/ui/label";
export { Select } from "@/components/ui/select";
export { Badge };

// ─────────────────────────── Card (title/actions convenience) ───────────────
// Thin wrapper over the composual shadcn Card so existing `<Card title actions>`
// call sites keep working.
export function Card({
  title,
  actions,
  children,
  className,
  bodyClassName,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <CardRoot className={cn("overflow-hidden", className)}>
      {(title || actions) && (
        <CardHeader>
          {title ? <CardTitle>{title}</CardTitle> : <span />}
          {actions}
        </CardHeader>
      )}
      <CardContent className={bodyClassName}>{children}</CardContent>
    </CardRoot>
  );
}

// ─────────────────────────── PageHeader ───────────────────────────

export function PageHeader({
  title,
  subtitle,
  eyebrow,
  actions,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  eyebrow?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-7 flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        {eyebrow && <div className="eyebrow mb-2">{eyebrow}</div>}
        <h1 className="font-display text-2xl font-semibold tracking-tight text-foreground">
          {title}
        </h1>
        {subtitle && <p className="mt-1.5 text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

// ─────────────────────────── Table style tokens ───────────────────────────
// Shared className tokens so the three list tables stay visually identical.

export const table = {
  wrap: "overflow-hidden rounded-xl border border-white/[0.07] bg-card/50 shadow-panel backdrop-blur-md",
  scroll: "scrollbar-thin overflow-x-auto",
  table: "w-full text-sm",
  head: "border-b border-white/[0.07] bg-white/[0.02] text-left font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground",
  th: "whitespace-nowrap px-4 py-2.5 font-medium",
  body: "divide-y divide-white/[0.05]",
  row: "group cursor-pointer transition-colors hover:bg-primary/[0.06]",
  rowStatic: "transition-colors hover:bg-white/[0.02]",
  td: "px-4 py-3 align-middle",
} as const;

// ─────────────────────────── Avatar ───────────────────────────

export function Avatar({
  src,
  name,
  className,
}: {
  src?: string | null;
  name: string;
  className?: string;
}) {
  if (src) {
    return (
      <img
        src={src}
        alt=""
        className={cn(
          "rounded-full object-cover ring-1 ring-white/10",
          className ?? "h-7 w-7",
        )}
      />
    );
  }
  return (
    <span
      className={cn(
        "flex items-center justify-center rounded-full bg-gradient-to-br from-primary to-[hsl(186_85%_42%)] text-xs font-bold text-primary-foreground ring-1 ring-white/15",
        className ?? "h-7 w-7",
      )}
    >
      {name.slice(0, 1).toUpperCase()}
    </span>
  );
}

// ─────────────────────────── Spinner / states ───────────────────────────

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn("h-5 w-5 animate-spin", className)} />;
}

export function CenteredSpinner({ label }: { label?: string }) {
  return (
    <div className="flex h-full min-h-[40vh] flex-col items-center justify-center gap-3 text-muted-foreground">
      <Spinner className="h-6 w-6 text-primary" />
      {label && <p className="text-sm">{label}</p>}
    </div>
  );
}

export function ErrorState({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-2.5 rounded-xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive-soft">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
      <span>{message}</span>
    </div>
  );
}

export function EmptyState({
  title,
  hint,
  icon: Icon = Inbox,
}: {
  title: string;
  hint?: string;
  icon?: typeof Inbox;
}) {
  return (
    <div className="flex flex-col items-center rounded-xl border border-dashed border-white/10 bg-card/30 px-8 py-12 text-center">
      <span className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-white/5 ring-1 ring-white/10">
        <Icon className="h-5 w-5 text-muted-foreground" />
      </span>
      <p className="text-sm font-medium text-foreground">{title}</p>
      {hint && <p className="mt-1 max-w-xs text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

// ─────────────────────────── Health dot ───────────────────────────

export function HealthDot({
  health,
}: {
  health: "healthy" | "degraded" | "unreachable" | "unknown";
}) {
  const color =
    health === "healthy"
      ? "bg-emerald-400 shadow-[0_0_8px] shadow-emerald-500/60"
      : health === "degraded"
        ? "bg-amber-400 shadow-[0_0_8px] shadow-amber-500/60"
        : health === "unreachable"
          ? "bg-destructive shadow-[0_0_8px] shadow-destructive/60"
          : "bg-muted-foreground/50";
  return (
    <span className="inline-flex items-center gap-1.5 text-xs capitalize text-foreground/80">
      <span className={cn("h-2 w-2 rounded-full", color)} />
      {health}
    </span>
  );
}

// ─────────────────────────── Driver badge ───────────────────────────

export function DriverBadge({ driver }: { driver: string | null }) {
  if (!driver) return <span className="text-muted-foreground/50">—</span>;
  return (
    <Badge tone={driver === "cloudflare" ? "orange" : "primary"}>{driver}</Badge>
  );
}

// ─────────────────────────── Status badge ───────────────────────────

const STATUS_TONE: Record<
  string,
  "slate" | "primary" | "amber" | "emerald" | "rose" | "sky"
> = {
  live: "rose",
  scheduled: "primary",
  paused: "amber",
  offline: "slate",
  waiting: "slate",
  starting: "slate",
  ended: "slate",
};

export function StatusBadge({ status }: { status: string }) {
  const tone = STATUS_TONE[status] ?? "slate";
  return (
    <Badge tone={tone} className={status === "ended" ? "opacity-70" : undefined}>
      {status === "live" && (
        <span className="h-1.5 w-1.5 animate-pulse2 rounded-full bg-destructive" />
      )}
      {status}
    </Badge>
  );
}
